import { teacherProfileAtom } from '@openeducation/oe-it-jotai-atoms';
import { useEnvironmentContext } from '@openeducation/oe-it-providers';
import { LiveClassService } from '@openeducation/oe-it-services';
import type { LiveClass } from '@openeducation/oe-it-shared-types';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import type { OlderLiveClass } from '../interfaces/older-live-class';
import { QueryKeys } from '../interfaces/query-keys';
import { TeacherLiveClassService } from '../services/teacher-live-class.service';

interface SendGroupLiveClassFeedback {
  personId: number;
  classroomId: number;
  feedback: string;
}

export function useLiveClass() {
  const queryClient = useQueryClient();
  const { environment } = useEnvironmentContext();
  const teacherProfile = useAtomValue(teacherProfileAtom);

  const {
    data: nextLiveClasses,
    isLoading: isLoadingNextLiveClasses,
    error: nextLiveClassesError,
  } = useQuery<LiveClass[], AxiosError>(
    [QueryKeys.NextLiveClass, teacherProfile?.id],
    () => LiveClassService.getLiveClasses(environment, teacherProfile?.id),
    { enabled: !!teacherProfile?.id, refetchOnWindowFocus: true }
  );

  const {
    data: olderLiveClasses,
    error: olderLiveClassesError,
    isLoading: isLoadingOlderLiveClasses,
  } = useQuery<OlderLiveClass[] | undefined>(
    [QueryKeys.OlderLiveClass, teacherProfile?.id],
    () =>
      TeacherLiveClassService.getOlderLiveClasses(
        environment,
        teacherProfile?.id
      ),
    { enabled: !!teacherProfile?.id }
  );

  const { mutateAsync: sendGroupLiveClassFeedback } = useMutation(
    ({ classroomId, feedback, personId }: SendGroupLiveClassFeedback) => {
      return TeacherLiveClassService.sendGroupLiveClassFeedback(
        environment,
        personId,
        classroomId,
        feedback,
        teacherProfile?.id
      );
    },
    {
      onSuccess: (_, variables) => {
        const oldClasses = queryClient.getQueryData<OlderLiveClass[]>([
          QueryKeys.OlderLiveClass,
          teacherProfile?.id,
        ]);

        if (!oldClasses) {
          return;
        }

        const updatedOldClasses = oldClasses.map<OlderLiveClass>(
          (liveClass) => {
            if (liveClass.id === variables.classroomId) {
              return {
                ...liveClass,
                studentIdsAndFeedback: [
                  ...(liveClass.studentIdsAndFeedback || []),
                  { id: variables.personId, feedbackStatus: true },
                ],
              };
            }
            return liveClass;
          }
        );

        queryClient.setQueryData<OlderLiveClass[]>(
          [QueryKeys.OlderLiveClass, teacherProfile?.id],
          updatedOldClasses
        );
      },
    }
  );

  const msToNextClassBegin = nextLiveClasses?.reduce(
    (acc, curr) => Math.min(acc, curr.msToClassBegin),
    Infinity
  );

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (msToNextClassBegin !== Infinity) {
      timerId = setTimeout(() => {
        queryClient.invalidateQueries(QueryKeys.NextLiveClass);
      }, msToNextClassBegin);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [msToNextClassBegin, queryClient]);

  return {
    msToNextClassBegin,
    nextLiveClasses,
    isLoadingNextLiveClasses,
    nextLiveClassesError,
    olderLiveClasses,
    isLoadingOlderLiveClasses,
    olderLiveClassesError,
    sendGroupLiveClassFeedback,
  };
}
